/**
 * aiProvider — unified AI call layer with Anthropic-first + OpenAI fallback.
 *
 * All text-generation paths (course outline, AI Assist, narration) go through
 * here. On Anthropic 529 (overloaded) or equivalent network error, we
 * transparently retry against OpenAI GPT-4o so authors are not blocked.
 *
 * Surface parity:
 *   createMessage()  — non-streaming (aiAssist, narrationGenerator)
 *   streamMessage()  — streaming with onChunk callback (course outline)
 *
 * For ai.ts specifically: it manages its own Anthropic streaming call (because
 * it passes multi-part content with binary PDFs and web_search tools). On 529
 * it calls openaiStreamFallback() here with text-only content.
 *
 * Environment:
 *   Dev:  Anthropic → VITE_ANTHROPIC_API_KEY direct
 *         OpenAI    → VITE_OPENAI_API_KEY direct
 *   Prod: Anthropic → /api/anthropic proxy (injects ANTHROPIC_API_KEY)
 *         OpenAI    → /api/openai proxy   (injects OPENAI_API_KEY)
 */

import Anthropic from '@anthropic-ai/sdk'
import OpenAI from 'openai'

const isDev = import.meta.env.DEV

// ── Anthropic client ──────────────────────────────────────────────────────────
export const anthropicClient = new Anthropic({
  apiKey: isDev ? import.meta.env.VITE_ANTHROPIC_API_KEY : 'proxy',
  baseURL: isDev ? undefined : `${window.location.origin}/api/anthropic`,
  dangerouslyAllowBrowser: true,
})

// ── OpenAI client (fallback) ──────────────────────────────────────────────────
const openai = new OpenAI({
  apiKey: isDev ? (import.meta.env.VITE_OPENAI_API_KEY ?? 'no-key') : 'proxy',
  baseURL: isDev ? undefined : `${window.location.origin}/api/openai`,
  dangerouslyAllowBrowser: true,
})

// ── Models ────────────────────────────────────────────────────────────────────
export const ANTHROPIC_MODEL = 'claude-sonnet-4-6'
export const OPENAI_FALLBACK_MODEL = 'gpt-4o'

// ── Overloaded error detection ────────────────────────────────────────────────
export function isOverloaded(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return (
    msg.includes('529') ||
    msg.toLowerCase().includes('overloaded') ||
    msg.includes('overloaded_error')
  )
}

// ── Shared message type ───────────────────────────────────────────────────────
export interface AIMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface CreateMessageParams {
  system?: string
  messages: AIMessage[]
  max_tokens: number
  model?: string
  /**
   * Mark the system prompt cacheable. Worth it only when the same system
   * prompt is sent many times within minutes — a PowerPoint import sends one
   * per slide — and it is long enough to cache (1,024 tokens on Opus 4.8).
   * Parallel requests all write the cache; later ones read it.
   */
  cacheSystem?: boolean
}

export interface StreamMessageParams extends CreateMessageParams {
  onChunk: (text: string) => void
}

// ── Non-streaming (aiAssist, narrationGenerator) ──────────────────────────────

/**
 * Call Anthropic; on 529 overloaded, fall back to OpenAI GPT-4o.
 * Returns the full response text.
 */
export async function createMessage(params: CreateMessageParams): Promise<string> {
  try {
    return await _anthropicCreate(params)
  } catch (err) {
    if (!isOverloaded(err)) throw err
    console.warn('[aiProvider] Anthropic overloaded — falling back to OpenAI', err)
    return _openaiCreate(params)
  }
}

async function _anthropicCreate(params: CreateMessageParams): Promise<string> {
  const response = await anthropicClient.messages.create({
    model: params.model ?? ANTHROPIC_MODEL,
    max_tokens: params.max_tokens,
    ...(params.system ? { system: params.system } : {}),
    messages: params.messages,
  })
  return response.content[0].type === 'text' ? response.content[0].text.trim() : ''
}

async function _openaiCreate(params: CreateMessageParams): Promise<string> {
  const messages: OpenAI.Chat.ChatCompletionMessageParam[] = [
    ...(params.system ? [{ role: 'system' as const, content: params.system }] : []),
    ...params.messages.map(m => ({ role: m.role as 'user' | 'assistant', content: m.content })),
  ]
  const response = await openai.chat.completions.create({
    model: OPENAI_FALLBACK_MODEL,
    max_tokens: Math.min(params.max_tokens, 16384), // GPT-4o output cap
    messages,
  })
  return response.choices[0]?.message?.content?.trim() ?? ''
}

// ── Streaming ─────────────────────────────────────────────────────────────────

/**
 * Stream from Anthropic; on 529 overloaded, fall back to OpenAI streaming.
 * For simple string-content messages only. ai.ts uses openaiStreamFallback()
 * directly because it handles multi-part content (PDFs) natively.
 */
export async function streamMessage(params: StreamMessageParams): Promise<string> {
  try {
    return await _anthropicStream(params)
  } catch (err) {
    if (!isOverloaded(err)) throw err
    console.warn('[aiProvider] Anthropic overloaded — falling back to OpenAI stream', err)
    return openaiStreamFallback(params)
  }
}

async function _anthropicStream(params: StreamMessageParams): Promise<string> {
  let fullText = ''
  const stream = await anthropicClient.messages.stream({
    model: params.model ?? ANTHROPIC_MODEL,
    max_tokens: params.max_tokens,
    ...(params.system
      ? { system: params.cacheSystem ? [{ type: 'text' as const, text: params.system, cache_control: { type: 'ephemeral' as const } }] : params.system }
      : {}),
    messages: params.messages,
  })
  for await (const chunk of stream) {
    if (chunk.type === 'content_block_delta' && chunk.delta.type === 'text_delta') {
      fullText += chunk.delta.text
      params.onChunk(chunk.delta.text)
    }
  }
  return fullText
}

/**
 * OpenAI streaming fallback — used directly by ai.ts when its native
 * Anthropic call fails with 529. Accepts text-only content (PDFs are
 * dropped for the fallback path; web_search is not available on OpenAI).
 */
export async function openaiStreamFallback(params: StreamMessageParams): Promise<string> {
  const messages: OpenAI.Chat.ChatCompletionMessageParam[] = [
    ...(params.system ? [{ role: 'system' as const, content: params.system }] : []),
    ...params.messages.map(m => ({ role: m.role as 'user' | 'assistant', content: m.content })),
  ]
  let fullText = ''
  const stream = await openai.chat.completions.create({
    model: OPENAI_FALLBACK_MODEL,
    max_tokens: Math.min(params.max_tokens, 16384),
    messages,
    stream: true,
  })
  for await (const chunk of stream) {
    const text = chunk.choices[0]?.delta?.content ?? ''
    if (text) {
      fullText += text
      params.onChunk(text)
    }
  }
  return fullText
}
